import {
  AuthResponse,
  ConfirmationResponse,
  ErrorResponse,
  ForgetPasswordResponse,
  IClientUserApiCall,
  IRestApiService,
  NewUser,
  RequestChangeEmailResponse,
  ResendConfirmationResponse,
  ResetPasswordData,
  ResetPasswordResponse,
  SignUpResponse,
  UpdateEmailData,
  UpdateEmailResponse,
  UpdatePasswordResponse,
  UpdateUserResponse,
  UserCredentials,
  UserInfo,
  UserUpdateData,
} from '../types';
import { TokenStorage } from '../infrastructure/TokenStorage';

class ClientUserApiCall implements IClientUserApiCall {
  private restApiService: IRestApiService;

  constructor(restApiService: IRestApiService) {
    this.restApiService = restApiService;
  }

  login(UserCredentials: UserCredentials): Promise<AuthResponse> {
    return new Promise<AuthResponse>((resolve, reject) => {
      this.restApiService.post('client-users/login', UserCredentials).then(
        (data: AuthResponse) => {
          TokenStorage.setSession(data.token, (data as any).refreshToken ?? null, 'client-users/refresh');
          resolve(data);
        },
        (err: ErrorResponse) => reject(err)
      );
    });
  }

  facebook(fbPayload: any, domain: string): Promise<AuthResponse> {
    return new Promise<AuthResponse>((resolve, reject) => {
      this.restApiService.post('client-users/facebook', { fbPayload, domain }).then(
        (data: AuthResponse) => {
          TokenStorage.setSession(data.token, (data as any).refreshToken ?? null, 'client-users/refresh');
          resolve(data);
        },
        (err: ErrorResponse) => reject(err)
      );
    });
  }

  google(googlePayload: any, domain: string): Promise<AuthResponse> {
    return new Promise<AuthResponse>((resolve, reject) => {
      this.restApiService.post('client-users/google', { googlePayload, domain }).then(
        (data: AuthResponse) => {
          TokenStorage.setSession(data.token, (data as any).refreshToken ?? null, 'client-users/refresh');
          resolve(data);
        },
        (err: ErrorResponse) => reject(err)
      );
    });
  }

  logout(): Promise<void> {
    const refreshToken = TokenStorage.getRefreshToken();
    TokenStorage.clearSession();
    if (refreshToken) {
      return this.restApiService.post('client-users/logout', { refreshToken }).catch(() => {});
    }
    return Promise.resolve();
  }

  fbLogin(token: string): void {
    TokenStorage.setSession(token, null, 'client-users/refresh');
  }

  connect(ownerId: string, studioClientId: string): Promise<AuthResponse> {
    return new Promise<AuthResponse>((resolve, reject) => {
      return this.restApiService.post(`client-users/connect/${ownerId}/${studioClientId}`).then(
        (data: AuthResponse) => {
          TokenStorage.setSession(data.token, (data as any).refreshToken ?? null, 'client-users/refresh');
          resolve(data);
        },
        (err: ErrorResponse) => reject(err)
      );
    });
  }

  killSession(): void {
    TokenStorage.clearSession();
  }

  isTokenStillThere(): boolean | undefined {
    try {
      return localStorage.getItem('token') !== null;
    } catch (e) {
      console.error(e);
      return undefined;
    }
  }

  setLang(lang: string): void {
    try {
      localStorage.setItem('voopty-lang', lang);
    } catch (e) {
      console.error(e);
    }
  }

  getUserInfo(): Promise<UserInfo> {
    return this.restApiService.get('client-users');
  }

  signUp(newUser: NewUser): Promise<SignUpResponse> {
    return this.restApiService.post('client-users', { user: newUser });
  }

  async checkPhonePin(ownerId: string, phone: string, pin: string): Promise<AuthResponse> {
    const url = `client-users/${ownerId}/check-phone-pin`;
    const data = await this.restApiService.post(url, {
      phone,
      pin,
    });
    if (data.token) {
      localStorage.setItem('token', data.token);
    }
    return data;
  }

  confirmUser(token: string): Promise<ConfirmationResponse> {
    return this.restApiService.post('client-users/confirm/' + token);
  }

  resetPassword(data: ResetPasswordData): Promise<ResetPasswordResponse> {
    return this.restApiService.post('client-users/resetPassword/', data);
  }

  forgetPassword(email: string, domain: string): Promise<ForgetPasswordResponse> {
    return this.restApiService.post('client-users/requestResetPassword/', {
      email,
      domain,
    });
  }

  resendConfirmation(email: string): Promise<ResendConfirmationResponse> {
    return this.restApiService.post('client-users/resend', { email });
  }

  updateUser(user: UserUpdateData): Promise<UpdateUserResponse> {
    return this.restApiService.put('client-users', { user });
  }

  updatePassword(oldPassword: string, newPassword: string): Promise<UpdatePasswordResponse> {
    return this.restApiService.put('client-users/pass', {
      oldPassword,
      newPassword,
    });
  }

  requestChangeEmail(newEmail: string): Promise<RequestChangeEmailResponse> {
    return this.restApiService.post('client-users/requestChangeEmail/', {
      newEmail,
    });
  }

  updateEmail(data: UpdateEmailData): Promise<UpdateEmailResponse> {
    return this.restApiService.post('client-users/changeEmail/', data);
  }

  inShoes(email: string): Promise<AuthResponse> {
    return new Promise<AuthResponse>((resolve, reject) => {
      this.restApiService.post('client-users/inShoes/' + email).then(
        (data: AuthResponse) => {
          localStorage.setItem('token', data.token);
          resolve(data);
        },
        (err: ErrorResponse) => {
          reject(err);
        }
      );
    });
  }
}

export default ClientUserApiCall;
